/**
 * The Work board's bulk menu (tenant only): Approve, Reschedule (with Keep approved) and Remove approval
 * on the selected tasks. No Work-specific write exists: each runs the Visit board's own bulk action (its
 * dialog, server admission and background rows in the shared bulk executor) on the Visits the tasks sit on,
 * carrying the selected task rows per Visit (`actionData.rows`), so only those tasks are approved, moved or
 * withdrawn.
 */
import { inject, Injector } from '@angular/core';
import { ActivityEntityName, JobSecurityRight, VisitBulkActionId, WorkTaskScheduleRow } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import type { BulkCommandArgs, BulkCommandOutcome, EntityBoardBulkCommandsArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { EntityBulkActionExecutor } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import { ownsWorkTaskApproval } from '../presentation/work.task.detail';
import { selectWorkTaskScheduleVisits } from './work.task.schedule.visit.selection';

/** Runs one Visit bulk action for the selected task rows; resolves whether it started. */
type RunVisitAction = (actionId: VisitBulkActionId, label: string, rows: readonly Partial<WorkTaskScheduleRow>[]) => Promise<boolean>;

/** The Visit bulk actions offered here; each acts on the selected tasks only. */
const VISIT_ACTIONS: readonly { id: VisitBulkActionId; label: string; icon: string }[] = [
  { id: VisitBulkActionId.Approve, label: 'Approve…', icon: 'fa-stamp' },
  { id: VisitBulkActionId.Reschedule, label: 'Reschedule…', icon: 'fa-calendar' },
  { id: VisitBulkActionId.WithdrawApproval, label: 'Remove approval…', icon: 'fa-stamp' },
];

/** The board's `bulkCommands` factory; runs in the board's injection context on every selection change. */
export function workTaskScheduleBulkCommands(args: EntityBoardBulkCommandsArgs<WorkTaskScheduleRow>): Command[] {

  const rights = inject(RightsService);
  if (!rights.hasRights(JobSecurityRight.CanAccess) || !rights.hasRights(JobSecurityRight.Manage)) return [];
  const selection = selectWorkTaskScheduleVisits(args.selectedItems ?? []);
  if (!selection.visits.length) return [];
  const run = workTaskVisitActionRunner();

  // Removal needs a selected task an approval owns; auto-approval is the line's policy, not a membership.
  const anyOwned = (args.selectedItems ?? []).some(ownsWorkTaskApproval);
  return VISIT_ACTIONS
    .filter(action => action.id !== VisitBulkActionId.WithdrawApproval || anyOwned)
    .map(action => new Command({ _id: action.id, label: action.label, iconLeft: `fa-regular ${action.icon}`, data: { run } }));
}

/** The board's `bulk.onCommand`: claims the Work commands; unknown ids fall through to the board's default. */
export async function handleWorkTaskScheduleBulkCommand(args: BulkCommandArgs<WorkTaskScheduleRow>): Promise<BulkCommandOutcome | false> {

  const action = VISIT_ACTIONS.find(entry => entry.id === args.action);
  const run = (args.commandData as { run?: RunVisitAction } | undefined)?.run;
  if (!action || !run) return false;

  return { handled: true, clearSelection: await run(action.id, action.label, args.data) };
}

/**
 * Stamped in an injection context (the board's for the bulk menu, the Work task panel's for its one task);
 * the action runs outside it. The request names Visits, so the executor opens the Visit manifest's dialog for
 * the action and queues it on the Visit bulk endpoint. On completion the Work, Visit and Upcoming reads are
 * invalidated, which refreshes the board and the task panel on it.
 */
export function workTaskVisitActionRunner(): RunVisitAction {

  const injector = inject(Injector);
  const executor = inject(EntityBulkActionExecutor);
  const data = inject(EntityDataService);
  const toasts = inject(ToastService);

  return async (actionId, label, rows) => {
    const selection = selectWorkTaskScheduleVisits(rows);
    if (!selection.visits.length) {
      toasts.error(label, selection.note ?? 'None of the selected tasks can be changed here.');
      return false;
    }
    if (selection.note) toasts.info(label, selection.note);
    let started = false;
    await executor.execute({
      context: 'board',
      entity: ActivityEntityName.Visit,
      args: {
        action: actionId,
        data: selection.visits,
        bulkActionRequest: {
          entity: ActivityEntityName.Visit,
          entityId: selection.visits.map(visit => visit._id),
          actionId,
          actionLabel: label,
          // Task rows by Visit: the Visit dialog keeps them, and the server acts on these rows only.
          actionData: { rows: selection.rows },
        },
      },
      injector,
      onStarted: () => started = true,
      onCompleted: () => {
        data.invalidate(ActivityEntityName.WorkTaskSchedule);
        data.invalidate(ActivityEntityName.Visit);
        void data.invalidateItems(ActivityEntityName.UpcomingWork);
      },
    });

    return started;
  };
}
